/***
 *
 *   PROJECT AUTH LAYOUT
 *   The white-labelled sign-in door for a private project: /project/:name/magic.
 *
 *   Why this is a separate layout rather than a branch inside AuthLayout —
 *   `layout: 'auth'` is shared by eleven routes in routes/auth.js that have no
 *   project in scope, and making every signin and signup render depend on a
 *   route param that is normally absent would be a poor trade for one route.
 *   AuthLayout is untouched.
 *
 *   It reuses auth.tailwind.js verbatim, which is the point: the branding panel
 *   is `from-primary-800 to-primary-900` with `text-white` on it, so inside
 *   BrandScope it becomes the tenant's CLAMPED 800/900 stops (600 on the public
 *   page is the hex as-is; this panel is why 800-900 still darken). Try a
 *   yellow brand here and the white heading has to stay readable.
 *
 *   What goes: the drifting-ticket backdrop (Valetify's own motif), the icon,
 *   the "Valetify" heading. What stays: the pinned theme toggle, and a "Powered
 *   by Valetify" line — this is the page most exposed to credential-phishing
 *   use, so attribution matters more here, not less.
 *
 **********/

import { useParams } from 'react-router-dom';
import {
    BrandScope,
    Button,
    Link,
    ProjectIdentityMark,
    Skeleton,
    useTheme,
} from 'components/lib';
import { useProjectBrand } from 'components/hooks/branding';
import { useTranslate } from 'app/translations';
import { rootPath } from 'utilities/routes';
import Style from '../auth/auth.tailwind.js';

export function ProjectAuthLayout(props) {
    const { isDark, toggleTheme } = useTheme();
    const { _t } = useTranslate();
    const { name } = useParams();
    const { project, brandColor, isLoading } = useProjectBrand(name);

    return (
        <BrandScope color={brandColor} className={Style.scope}>
            <div className={Style.shell}>
                <aside className={Style.branding}>
                    <div className={Style.brandingCopy}>
                        {isLoading ? (
                            <Skeleton className="mx-auto mb-8 h-14 w-40" />
                        ) : (
                            <ProjectIdentityMark
                                size="lg"
                                name={project?.name}
                                filePath={project?.file_path}
                                alt={project?.name}
                                className="mx-auto mb-8 object-center"
                                // On the gradient panel the muted fallback
                                // would disappear, so invert it — same
                                // `bg-white/15` convention the sidebar uses.
                                fallbackClassName="mx-auto mb-8 bg-white/15 text-white"
                            />
                        )}
                        <h1 className={Style.brandTitle}>{project?.name || ''}</h1>
                        <p className={Style.brandTagline}>
                            {_t('publicIntake.brand.signinTagline')}
                        </p>
                    </div>
                </aside>

                <div className={Style.formPanel}>
                    <div className={Style.themePin}>
                        <Button
                            variant="ghost"
                            size="sm"
                            icon={isDark ? 'sun' : 'moon'}
                            iconButton
                            ariaLabel={_t(
                                isDark
                                    ? 'landing.nav.theme.toLight'
                                    : 'landing.nav.theme.toDark',
                            )}
                            action={toggleTheme}
                        />
                    </div>
                    <div className={Style.formInner}>
                        <props.children {...props.data} />
                    </div>
                    <Link
                        url={rootPath('/')}
                        text={`${_t('poweredBy')} Valetify`}
                        className={Style.attribution}
                    />
                </div>
            </div>
        </BrandScope>
    );
}
